//-- copyright
// GPE customization (gov.ao)
//
// Color helpers mirroring app/models/colors/hex_color.rb and the derived variables
// generated in app/views/custom_styles/_inline_css.erb.
//++

export interface Rgb {
  r:number;
  g:number;
  b:number;
}

export function parseHex(hex:string):Rgb|null {
  const normalized = hex.trim().toUpperCase().replace(/^#/, '');

  if (!/^[0-9A-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function blendChannel(channel:number, mixValue:number, opacity:number):number {
  return Math.min(255, Math.round((channel * opacity) + (mixValue * (1 - opacity))));
}

function toHex({ r, g, b }:Rgb):string {
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

export function darken(hex:string, amount:number):string {
  const rgb = parseHex(hex);
  if (!rgb) {
    return hex;
  }

  const opacity = 1 - amount;
  return toHex({
    r: blendChannel(rgb.r, 0, opacity),
    g: blendChannel(rgb.g, 0, opacity),
    b: blendChannel(rgb.b, 0, opacity),
  });
}

export function lighten(hex:string, amount:number):string {
  const rgb = parseHex(hex);
  if (!rgb) {
    return hex;
  }

  const opacity = 1 - amount;
  return toHex({
    r: blendChannel(rgb.r, 255, opacity),
    g: blendChannel(rgb.g, 255, opacity),
    b: blendChannel(rgb.b, 255, opacity),
  });
}

export function brightnessYiq(hex:string):number {
  const rgb = parseHex(hex);
  if (!rgb) {
    return 0;
  }

  return ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
}

export function isBright(hex:string):boolean {
  return brightnessYiq(hex) >= 150;
}

export function contrastingFontColor(hex:string, light = '#FFFFFF', dark = '#333333'):string {
  return isBright(hex) ? dark : light;
}

export function applyDesignColor(root:HTMLElement, variable:string, hex:string):void {
  root.style.setProperty(`--${variable}`, hex);

  switch (variable) {
    case 'header-bg-color':
      root.style.setProperty('--header-item-font-color', contrastingFontColor(hex));
      if (isBright(hex)) {
        root.style.setProperty('--header-border-bottom-width', '1px');
        root.style.setProperty('--header-border-bottom-color', '#d0d7de');
      }
      break;
    case 'header-item-bg-hover-color':
      root.style.setProperty('--header-item-font-hover-color', contrastingFontColor(hex));
      break;
    case 'main-menu-bg-color':
      root.style.setProperty('--main-menu-font-color', contrastingFontColor(hex));
      if (isBright(hex)) {
        root.style.setProperty('--main-menu-border-width', '1px');
        root.style.setProperty('--main-menu-border-color', '#d0d7de');
      }
      break;
    case 'main-menu-bg-selected-background':
      root.style.setProperty('--main-menu-selected-font-color', contrastingFontColor(hex));
      break;
    case 'main-menu-bg-hover-background':
      root.style.setProperty('--main-menu-hover-font-color', contrastingFontColor(hex));
      break;
    case 'primary-button-color':
      root.style.setProperty('--link-color', hex);
      root.style.setProperty('--primary-button-color--major1', darken(hex, 0.18));
      root.style.setProperty('--primary-button-color--minor1', lighten(hex, 0.8));
      root.style.setProperty('--primary-button-color--minor2', lighten(hex, 0.6));
      root.style.setProperty('--primary-button-color--dark-mode', lighten(hex, 0.4));
      root.style.setProperty('--font-color-on-primary', contrastingFontColor(hex));
      if (isBright(hex)) {
        root.style.setProperty('--button--primary-border-color', '#d0d7de');
      }
      break;
    case 'accent-color':
      root.style.setProperty('--accent-color--major1', darken(hex, 0.2));
      root.style.setProperty('--accent-color--major2', darken(hex, 0.4));
      root.style.setProperty('--accent-color--minor1', lighten(hex, 0.8));
      root.style.setProperty('--accent-color--minor2', lighten(hex, 0.6));
      root.style.setProperty('--accent-color--dark-mode', lighten(hex, 0.4));
      break;
    default:
      break;
  }
}
